import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "State/hooks";
import { useGetMeQuery } from "Services/user";
import { useGetPipelinesQuery } from "Services/pipeline";
import { Pipeline } from "Models/pipeline";

export interface PipelineSelection {
  pipelines: Pipeline[];
  /** Explicit choice, else the profile default, else the first CRM pipeline. */
  pipelineId: string | undefined;
  pipeline: Pipeline | undefined;
  /** Name to show for the selected pipeline. */
  label: string | undefined;
  select: (pipelineId: string) => void;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

/**
 * One pipeline selection shared by the revenue snapshot and the stage chart,
 * so both cards always describe the same pipeline. Resets on tenant change.
 */
export function usePipelineSelection(): PipelineSelection {
  const companyId = useAppSelector(s => s.company.companyId);
  const { data: profile } = useGetMeQuery();
  const ready = !!companyId && profile?.companyId === companyId;
  const { currentData, isLoading, isFetching, isError, refetch } = useGetPipelinesQuery(companyId, { skip: !ready });
  const [selected, setSelected] = useState("");
  useEffect(() => { setSelected(""); }, [companyId]);

  const pipelines = useMemo(() => (ready ? currentData ?? [] : []), [ready, currentData]);
  const pipeline = pipelines.find(p => p.sourcePipelineId === selected)
    ?? pipelines.find(p => p.sourcePipelineId === profile?.defaultPipeline)
    ?? pipelines[0];
  // Without a pipeline list, an explicit choice or the stored preference still identifies the pipeline.
  const pipelineId = pipeline?.sourcePipelineId ?? (ready ? selected || profile?.defaultPipeline || undefined : undefined);

  return {
    pipelines,
    pipelineId,
    pipeline,
    label: pipeline?.label,
    select: setSelected,
    isLoading: ready ? isLoading || (isFetching && !currentData) : !isError,
    isError,
    retry: () => { refetch(); },
  };
}
